import React, { useEffect, useMemo, useState } from "react";
import { Inbox, Plus, Filter, Check, X, Plane, Users, HelpCircle, GraduationCap, Laptop, MoreHorizontal, ClipboardList, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import EmployeeRequestModal from "@/components/requests/EmployeeRequestModal";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_ICON = {
  "Leave Request": Plane,
  "Meeting Request": Users,
  "Assistance Request": HelpCircle,
  "Training Request": GraduationCap,
  "Equipment Request": Laptop,
  "Other": MoreHorizontal,
};

const CATEGORY_TONE = {
  "Leave Request": "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-500/25",
  "Meeting Request": "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/25",
  "Assistance Request": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  "Training Request": "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  "Equipment Request": "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/25",
  "Other": "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/25",
};

const STATUS_TONE = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  Declined: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/25",
};

const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function EmployeeRequestsPage() {
  const [user, setUser] = useState({ name: "", email: "" });
  const [consultants, setConsultants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("mine");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [decideModal, setDecideModal] = useState({ open: false, request: null, action: "approve", note: "" });

  const isManager = useMemo(() => consultants.find((c) => c.name === user.name)?.is_manager, [consultants, user]);

  const load = () => endpoints.listEmployeeRequests().then(setRequests).catch(() => {});

  useEffect(() => {
    endpoints.me().then(setUser).catch(() => {});
    endpoints.listConsultants().then(setConsultants);
    endpoints.requestCategories().then((r) => { setCategories(r.categories); setLeaveTypes(r.leave_types); });
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...requests];
    if (tab === "mine") list = list.filter((r) => r.employee === user.name);
    else if (tab === "inbox") list = list.filter((r) => r.manager === user.name);
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((r) => `${r.employee} ${r.category} ${r.subject || ""} ${r.leave_type || ""} ${r.notes || ""}`.toLowerCase().includes(s));
    }
    return list;
  }, [requests, tab, statusFilter, q, user.name]);

  const stats = useMemo(() => {
    const inbox = requests.filter((r) => r.manager === user.name);
    return {
      pending: inbox.filter((r) => r.status === "Pending").length,
      approved: inbox.filter((r) => r.status === "Approved").length,
      declined: inbox.filter((r) => r.status === "Declined").length,
      mine: requests.filter((r) => r.employee === user.name).length,
    };
  }, [requests, user.name]);

  const submitDecision = async () => {
    const { request, action, note } = decideModal;
    try {
      await endpoints.decideEmployeeRequest(request.id, { action, note, decided_by: user.name });
      toast.success(action === "approve" ? "Request approved · employee notified" : "Request declined · employee notified");
      setDecideModal({ open: false, request: null, action: "approve", note: "" });
      load();
    } catch {
      toast.error("Could not save decision");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-indigo-500/25">
            <ClipboardList className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Employee Requests</h1>
            <p className="text-xs text-slate-500">Leave, meetings, assistance, training & equipment</p>
          </div>
        </div>
        <Button data-testid="er-new-button" className="brand-gradient text-white ml-auto" onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New request
        </Button>
      </div>

      {isManager && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div data-testid="er-stat-pending" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Awaiting my decision</div>
            <div className="text-2xl font-extrabold font-display mt-1 text-amber-600">{stats.pending}</div>
          </div>
          <div data-testid="er-stat-approved" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Approved</div>
            <div className="text-2xl font-extrabold font-display mt-1 text-emerald-600">{stats.approved}</div>
          </div>
          <div data-testid="er-stat-declined" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Declined</div>
            <div className="text-2xl font-extrabold font-display mt-1 text-rose-600">{stats.declined}</div>
          </div>
          <div data-testid="er-stat-mine" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">My requests</div>
            <div className="text-2xl font-extrabold font-display mt-1 text-indigo-600">{stats.mine}</div>
          </div>
        </div>
      )}

      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="er-tabs" className="h-9 bg-slate-100 dark:bg-slate-800">
            <TabsTrigger value="mine" className="text-xs h-7" data-testid="er-tab-mine">My requests</TabsTrigger>
            {isManager && <TabsTrigger value="inbox" className="text-xs h-7 relative" data-testid="er-tab-inbox">
              Manager inbox
              {stats.pending > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[9px] px-1.5 py-0.5 font-bold">{stats.pending}</span>}
            </TabsTrigger>}
            <TabsTrigger value="all" className="text-xs h-7" data-testid="er-tab-all">All history</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input data-testid="er-search-input" placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" />
        <div className="flex items-center gap-1.5 ml-auto">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="er-status-filter" className="h-9 w-[150px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">
          <Inbox className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          No requests to show.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const Icon = CATEGORY_ICON[r.category] || Inbox;
            const canDecide = isManager && r.manager === user.name && r.status === "Pending";
            return (
              <div key={r.id} data-testid={`er-row-${r.id}`} className="glass-card rounded-2xl p-4 flex items-start gap-4">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 border ${CATEGORY_TONE[r.category] || ""}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">
                      {r.category}
                      {r.leave_type ? ` · ${r.leave_type}` : ""}
                      {r.subject ? ` · ${r.subject}` : ""}
                    </span>
                    <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[r.status] || ""}`}>{r.status}</Badge>
                    {r.email_sent && r.status !== "Pending" && (
                      <Badge variant="outline" className="text-[10px] rounded-full gap-1 bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600">
                        <Mail className="h-2.5 w-2.5" /> emailed
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1 flex-wrap">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="brand-gradient text-white text-[9px] font-semibold">{initials(r.employee)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-slate-600 dark:text-slate-300">{r.employee}</span>
                    <span>→</span>
                    <span>{r.manager}</span>
                    <span className="text-slate-300">·</span>
                    <span>{timeAgo(r.created_at)}</span>
                    {r.start_date && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="font-medium">{fmtDate(r.start_date)} → {fmtDate(r.end_date)} ({r.working_days} wd)</span>
                      </>
                    )}
                  </div>
                  {r.notes && <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">{r.notes}</p>}
                  {r.decision_note && (
                    <div className="mt-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 px-3 py-2 text-xs">
                      <span className="font-semibold">{r.decided_by} · {r.status.toLowerCase()}:</span> {r.decision_note}
                    </div>
                  )}
                </div>
                {canDecide && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      data-testid={`er-approve-${r.id}`}
                      size="sm"
                      className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setDecideModal({ open: true, request: r, action: "approve", note: "" })}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      data-testid={`er-decline-${r.id}`}
                      size="sm"
                      variant="outline"
                      className="h-8 border-rose-200 text-rose-600 hover:bg-rose-50"
                      onClick={() => setDecideModal({ open: true, request: r, action: "decline", note: "" })}
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Decline
                    </Button>
                    <Button
                      data-testid={`er-delete-${r.id}`}
                      size="sm"
                      variant="ghost"
                      className="h-8 text-slate-500 hover:text-rose-600"
                      onClick={async () => {
                        if (!window.confirm("Delete this request?")) return;
                        try { await endpoints.deleteEmployeeRequest(r.id); toast.success("Request deleted"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                )}
                {!canDecide && r.employee_email === user?.email && (
                  <Button
                    data-testid={`er-delete-mine-${r.id}`}
                    size="sm"
                    variant="ghost"
                    className="h-8 text-slate-500 hover:text-rose-600 shrink-0"
                    onClick={async () => {
                      if (!window.confirm("Delete this request?")) return;
                      try { await endpoints.deleteEmployeeRequest(r.id); toast.success("Request deleted"); load(); }
                      catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <EmployeeRequestModal
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={() => { setNewOpen(false); load(); }}
        currentUser={user}
        consultants={consultants}
        categories={categories}
        leaveTypes={leaveTypes}
      />

      <Dialog open={decideModal.open} onOpenChange={(o) => setDecideModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="modal-decide-request" className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{decideModal.action === "approve" ? "Approve request" : "Decline request"}</DialogTitle>
            <DialogDescription>
              {decideModal.action === "approve" ? "The employee will be notified and any approved leave will appear on the team calendar." : "The employee will be notified with your reason."}
            </DialogDescription>
          </DialogHeader>
          {decideModal.request && (
            <div className="text-sm">
              <div className="font-semibold">{decideModal.request.employee} · {decideModal.request.category}</div>
              {decideModal.request.leave_type && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {decideModal.request.leave_type} · {fmtDate(decideModal.request.start_date)} → {fmtDate(decideModal.request.end_date)} ({decideModal.request.working_days} working days)
                </div>
              )}
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500">Message to employee (optional)</label>
            <Textarea
              data-testid="er-decision-note-input"
              rows={3}
              value={decideModal.note}
              onChange={(e) => setDecideModal((m) => ({ ...m, note: e.target.value }))}
              placeholder={decideModal.action === "approve" ? "Enjoy your time off!" : "Reason for declining..."}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecideModal({ open: false, request: null, action: "approve", note: "" })}>Cancel</Button>
            <Button
              data-testid="er-confirm-decision"
              className={decideModal.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
              onClick={submitDecision}
            >
              {decideModal.action === "approve" ? <Check className="h-4 w-4 mr-1" /> : <X className="h-4 w-4 mr-1" />}
              Confirm {decideModal.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
